/**
 * Leave reports and holiday bulk import (Phase 4d).
 *
 * A SEPARATE ROUTER, SAME `/leave` PREFIX as the other leave contracts, no path overlap with them
 * (`/reports/*` and `/holidays/import/*`; the only other `/holidays/*` routes use other methods).
 *
 * REPORTS are read-only and gated on `leave.report:read`, reaching the people the caller's grants
 * reach. The paged JSON reads are safe for the copilot; the CSV export is a bulk download and is
 * hidden from it (`ai: 'hidden'`, meta.ts reason 1: it is the whole dataset, not an answer).
 *
 * HOLIDAY IMPORT is `holiday_calendar:manage`, as every other holiday write. Both routes carry a
 * CSV's text or a parsed table, which a model cannot meaningfully produce (meta.ts reason 2), so
 * both are hidden from the copilot. Apply takes an `Idempotency-Key`: a retried upload returns the
 * original result, and re-uploading the same file with a new key creates nothing (existing
 * location-and-date pairs are skipped).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  holidayImportBodySchema,
  holidayImportPreviewSchema,
  holidayImportResultSchema,
  leaveLapseReportQuerySchema,
  leaveLapseReportSchema,
  leaveLopReportQuerySchema,
  leaveLopReportRowSchema,
  leaveRegisterExportQuerySchema,
  leaveRegisterReportQuerySchema,
  leaveRegisterReportRowSchema,
  leaveReportCsvExportSchema,
  leaveTrendsQuerySchema,
  leaveTrendsReportSchema,
} from '../leave-reports.js';
import { errorBodySchema, idempotencyKeySchema, paginatedSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

// The schemas and types live in `../leave-reports.ts`; re-exported here so the one contracts barrel
// carries them to `@hrms/shared`.
export * from '../leave-reports.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const leaveReportsContract = c.router(
  {
    // ---- Reports ------------------------------------------------------------------------
    getLeaveRegisterReport: {
      method: 'GET',
      path: '/reports/register',
      query: leaveRegisterReportQuerySchema,
      responses: {
        200: paginatedSchema(leaveRegisterReportRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        /** Period reversed or longer than a year. */
        422: errorBodySchema,
      },
      summary:
        'Leave register for a period: per person and leave type, opening, accrued, carried ' +
        'forward, adjusted, used, encashed, lapsed and closing days',
      metadata: routeMeta('leave.report:read'),
    },

    exportLeaveRegisterReport: {
      method: 'GET',
      path: '/reports/register/export',
      query: leaveRegisterExportQuerySchema,
      responses: {
        200: leaveReportCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'The leave register as a CSV file (capped; says so when it is cut)',
      metadata: routeMeta('leave.report:read', { ai: 'hidden' }),
    },

    getLeaveLapseReport: {
      method: 'GET',
      path: '/reports/lapses',
      query: leaveLapseReportQuerySchema,
      responses: {
        200: leaveLapseReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Period reversed or longer than a year, or too many ledger rows to scan. */
        422: errorBodySchema,
      },
      summary:
        'Days that lapsed, expired or were forfeited at a year rollover in a period, ' +
        'from the leave ledger',
      metadata: routeMeta('leave.report:read'),
    },

    getLeaveTrendsReport: {
      method: 'GET',
      path: '/reports/trends',
      query: leaveTrendsQuerySchema,
      responses: {
        200: leaveTrendsReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Period reversed or longer than two years, or too many requests to aggregate. */
        422: errorBodySchema,
      },
      summary: 'Leave requests and days by month, department and leave type',
      metadata: routeMeta('leave.report:read'),
    },

    getLeaveLopReport: {
      method: 'GET',
      path: '/reports/loss-of-pay',
      query: leaveLopReportQuerySchema,
      responses: {
        200: paginatedSchema(leaveLopReportRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Unpaid leave per person for a period, with the loss-of-pay days payroll would deduct ' +
        '(the same rule the payroll run uses)',
      metadata: routeMeta('leave.report:read'),
    },

    // ---- Holiday bulk import ------------------------------------------------------------
    previewHolidayImport: {
      method: 'POST',
      path: '/holidays/import/preview',
      body: holidayImportBodySchema,
      responses: {
        200: holidayImportPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Validate a holiday CSV or parsed rows and preview each row, writing nothing',
      metadata: routeMeta('holiday_calendar:manage', { ai: 'hidden' }),
    },

    applyHolidayImport: {
      method: 'POST',
      path: '/holidays/import/apply',
      headers: idempotencyHeadersSchema,
      body: holidayImportBodySchema,
      responses: {
        201: holidayImportResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        /** Any row with an error refuses the whole file; the preview says which. */
        422: errorBodySchema,
      },
      summary:
        'Create the holidays of a validated file atomically; existing location-and-date pairs ' +
        'are skipped (holiday_calendar:manage, Idempotency-Key required)',
      metadata: routeMeta('holiday_calendar:manage', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/leave' },
);
